import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
  IconFullscreen,
} from '../../../components';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import fullscreenButton from '../images/fullscreen.png';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};

const ListingImageGallery = props => {
  const {
    intl,
    rootClassName,
    className,
    images,
    videos,
    imageVariants,
    thumbnailVariants,
    outOfStockImage,
    onFullScreenChange,
  } = props;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imageGalleryRef = useRef(null);

  const setGalleryProps = isFullScreen => {
    const slideElements = document.getElementsByClassName('image-gallery-slides');

    onFullScreenChange( isFullScreen );

    if( slideElements ){
      [...slideElements].forEach( element => {
        element.style.cursor = isFullScreen ? 'default' : 'zoom-in';

        if( isFullScreen ){
          element.removeEventListener('click', handleToggleFullscreen );
        } else {
          element.addEventListener('click', handleToggleFullscreen);
        }
      });
    }
  };

  const handleToggleFullscreen = () => {
    if (imageGalleryRef.current) {
      imageGalleryRef.current.fullScreen( true ); // Toggles fullscreen based on current state
      setGalleryProps( true );
    }
  };

  useEffect(() => {
    setGalleryProps( false );
  }, []);

  const thumbVariants = thumbnailVariants || imageVariants;

  const imageItems = images.map((img, i) => {
    return {
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });

  const videoItems = videos.map((videoUrl, i) => ({
    original: '',
    alt: intl.formatMessage(
      { id: 'ListingImageGallery.videoAltText' },
      { index: i + 1, count: videos.length }
    ),
    thumbnail: [],
    video: videoUrl,
  }));

  const allItems = [...imageItems, ...videoItems];

  const renderItem = item => {
    if (item.video) {
      return (
        <AspectRatioWrapper
          width={1}
          height={1}
          aspectBoxClassName={isFullscreen ? css.itemWrapper : null}
        >
          <div className={isFullscreen ? css.itemCenteringFullScreen : css.itemCentering}>
            <video
              className={css.video}
              controls
              autoPlay={false}
              src={item.video}
              alt={item.alt}
            />
          </div>
        </AspectRatioWrapper>
      );
    }

    return (
      <AspectRatioWrapper
        width={1}
        height={1}
        className={css.itemWrapper}
      >
        <div className={isFullscreen ? css.itemCenteringFullScreen : css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
          />
        </div>
      </AspectRatioWrapper>
    );
  };


  const renderThumbInner = item => {
    if (item.video) {
      return (
        <div class={css.test}>
          <div className={css.thumb}>
            <video
              className={css.video}
              autoPlay={false}
              src={item.video}
              alt={item.alt}
            />
          </div>
        </div>
      );
    }
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen( isFull );
    setGalleryProps( isFull );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <ArrowBackIosNewIcon style={{ fontSize: '40px' }} />
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <ArrowForwardIosIcon style={{ fontSize: '40px' }} />
      </button>
    );
  };

  const renderCustomFullscreenButton = (onClick, isFullscreen) => {
    return (
      <button
        type="button"
        className="image-gallery-icon image-gallery-fullscreen-button"
        onClick={onClick}
        aria-label="Open Fullscreen"
      >
        <img src={fullscreenButton} alt="Fullscreen" width={64} height={64} />
      </button>
    );
  };

  if (allItems.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      {outOfStockImage}
      <ReactImageGallery
        additionalClass={classes}
        items={allItems}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        showFullscreenButton={isFullscreen}
        ref={imageGalleryRef}
        {...IMAGE_GALLERY_OPTIONS}
      />
    </>
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
